import { useEditor, EditorContent } from '@tiptap/react';
// Source imported this as '@tiptap/react/menus'. That subpath exists only in the
// package.json "exports" map, which webpack 4 (react-scripts 3.4.1) does not read,
// so the built file is addressed directly. Unlike the @tiptap/pm subpaths, nothing
// inside node_modules imports this one, so no resolution shim is needed for it.
import { BubbleMenu } from '@tiptap/react/dist/menus/index.js';
import StarterKit from '@tiptap/starter-kit';
import Paragraph from '@tiptap/extension-paragraph';
import { Edit2, X, Trash2, GitBranch, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, AlignLeft, AlignCenter, AlignRight } from './icons';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection, NodeSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { CustomOrderedList } from '../extensions/CustomOrderedList';
import { CustomBulletList } from './CustomBulletList';
import { VariableTag } from '../extensions/VariableTag';
import { ConditionalBlock } from '../extensions/ConditionalBlock';
import { OptionalBlock } from '../extensions/OptionalBlock';
import { ClearSpaceBlock } from '../extensions/ClearSpaceBlock';
import { DollarBlock } from '../extensions/DollarBlock';
import { PageBreakBlock, LineBreakBlock, HorizontalLineBlock } from '../extensions/LineRuleBlocks';
import { WhiteBlankImageBlock } from './WhiteBlankImageBlock';
import { SyntaxAutoSync } from './SyntaxAutoSync';
import { SmartSyntaxExtension } from '../extensions/SmartSyntaxExtension';
import { EditorToolbar, alignStatement } from './EditorToolbar';
import { RuleModal } from './RuleModal';
import { TableHoverControls } from './TableHoverControls';
import { SyntaxHelpPanel } from './SyntaxHelpPanel';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import Mention from '@tiptap/extension-mention';
import getSuggestion from '../extensions/suggestion';
import { getDraggedVariable, setDraggedVariable } from './dragState';
/**
 * TemplateEditor — Main editor wrapper that initializes TipTap
 * with custom extensions and handles drag-and-drop of variables.
 */
const searchHighlightPluginKey = new PluginKey('searchHighlight');

const SearchHighlightExtension = Extension.create({
    name: 'searchHighlight',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: searchHighlightPluginKey,
                state: {
                    init() {
                        return DecorationSet.empty;
                    },
                    apply(tr, oldSet) {
                        const newDecorations = tr.getMeta(searchHighlightPluginKey);
                        if (newDecorations !== undefined) {
                            return newDecorations;
                        }
                        if (tr.docChanged) {
                            return oldSet.map(tr.mapping, tr.doc);
                        }
                        return oldSet;
                    },
                },
                props: {
                    decorations(state) {
                        return searchHighlightPluginKey.getState(state);
                    },
                },
            }),
        ];
    },
});

const CustomParagraph = Paragraph.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            lineHeight: {
                default: null,
                parseHTML: element => {
                    const lh = element.style.lineHeight;
                    if (!lh) return null;
                    const num = parseFloat(lh);
                    if (lh.includes('pt') || (num > 0 && num < 1.5)) return null;
                    if (!isNaN(num) && num > 0) {
                        return lh;
                    }
                    return null;
                },
                renderHTML: attributes => {
                    if (!attributes.lineHeight) return {};
                    return { style: `line-height: ${attributes.lineHeight}` };
                }
            },
            marginTop: {
                default: null,
                parseHTML: element => {
                    const mt = element.style.marginTop;
                    if (!mt) return null;
                    const num = parseFloat(mt);
                    if (!isNaN(num) && num < 0) return null;
                    return mt;
                },
                renderHTML: attributes => {
                    if (!attributes.marginTop) return {};
                    return { style: `margin-top: ${attributes.marginTop}` };
                }
            },
            marginBottom: {
                default: null,
                parseHTML: element => {
                    const mb = element.style.marginBottom;
                    if (!mb) return null;
                    const num = parseFloat(mb);
                    if (!isNaN(num) && num < 0) return null;
                    return mb;
                },
                renderHTML: attributes => {
                    if (!attributes.marginBottom) return {};
                    return { style: `margin-bottom: ${attributes.marginBottom}` };
                }
            },
            textIndent: {
                default: null,
                parseHTML: element => element.style.textIndent || null,
                renderHTML: attributes => {
                    if (!attributes.textIndent) return {};
                    return { style: `text-indent: ${attributes.textIndent}` };
                }
            },
            marginLeft: {
                default: null,
                parseHTML: element => element.style.marginLeft || null,
                renderHTML: attributes => {
                    if (!attributes.marginLeft) return {};
                    return { style: `margin-left: ${attributes.marginLeft}` };
                }
            },
            paddingLeft: {
                default: null,
                parseHTML: element => element.style.paddingLeft || null,
                renderHTML: attributes => {
                    if (!attributes.paddingLeft) return {};
                    return { style: `padding-left: ${attributes.paddingLeft}` };
                }
            },
        };
    },
});

const CustomTable = Table.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            tableCondition: {
                default: null,
                parseHTML: element => element.getAttribute('data-table-condition'),
                renderHTML: attributes => {
                    if (!attributes.tableCondition) return {};
                    return { 'data-table-condition': attributes.tableCondition };
                }
            },
            tableConditionData: {
                default: null,
                parseHTML: element => element.getAttribute('data-table-condition-data'),
                renderHTML: attributes => {
                    if (!attributes.tableConditionData) return {};
                    return { 'data-table-condition-data': typeof attributes.tableConditionData === 'object' ? JSON.stringify(attributes.tableConditionData) : attributes.tableConditionData };
                }
            },
            columnConditions: {
                default: null,
                parseHTML: element => {
                    const raw = element.getAttribute('data-column-conditions');
                    if (!raw) return null;
                    try { return JSON.parse(raw); } catch { return null; }
                },
                renderHTML: attributes => {
                    if (!attributes.columnConditions) return {};
                    return { 'data-column-conditions': typeof attributes.columnConditions === 'object' ? JSON.stringify(attributes.columnConditions) : attributes.columnConditions };
                }
            }
        };
    }
});

const CustomTableRow = TableRow.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            rowCondition: {
                default: null,
                parseHTML: element => element.getAttribute('data-row-condition'),
                renderHTML: attributes => {
                    if (!attributes.rowCondition) return {};
                    return { 'data-row-condition': attributes.rowCondition };
                }
            },
            rowConditionData: {
                default: null,
                parseHTML: element => element.getAttribute('data-row-condition-data'),
                renderHTML: attributes => {
                    if (!attributes.rowConditionData) return {};
                    return { 'data-row-condition-data': typeof attributes.rowConditionData === 'object' ? JSON.stringify(attributes.rowConditionData) : attributes.rowConditionData };
                }
            }
        };
    }
});

const CustomTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            textAlign: {
                default: 'left',
                parseHTML: element => element.style.textAlign || 'left',
                renderHTML: attributes => {
                    return {
                        style: `text-align: ${attributes.textAlign || 'left'}`
                    };
                }
            }
        };
    }
});

const CustomTableHeader = TableHeader.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            textAlign: {
                default: 'left',
                parseHTML: element => element.style.textAlign || 'left',
                renderHTML: attributes => {
                    return {
                        style: `text-align: ${attributes.textAlign || 'left'}`
                    };
                }
            }
        };
    }
});

function findNearestTextblockPos(doc, pos) {
    pos = Math.max(0, Math.min(doc.content.size, pos));
    let r = doc.resolve(pos);
    if (r.parent.isTextblock) return pos;

    let forwardPos = null;
    doc.nodesBetween(pos, doc.content.size, (node, p) => {
        if (forwardPos === null && node.isTextblock) {
            forwardPos = p + 1;
            return false;
        }
    });

    let backwardPos = null;
    doc.nodesBetween(0, pos, (node, p) => {
        if (node.isTextblock) {
            backwardPos = p + 1 + node.content.size;
        }
    });

    if (forwardPos !== null && backwardPos !== null) {
        return Math.abs(forwardPos - pos) <= Math.abs(backwardPos - pos) ? forwardPos : backwardPos;
    }
    return forwardPos !== null ? forwardPos : (backwardPos !== null ? backwardPos : pos);
}

export function TemplateEditor({ onEditorReady, variables, initialContent, onVariablesExtracted, onContentChange }) {
    const variablesRef = useRef(variables);
    useEffect(() => {
        variablesRef.current = variables;
    }, [variables]);
    const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
    const [isSyntaxHelpOpen, setIsSyntaxHelpOpen] = useState(false);
    const [editingRuleId, setEditingRuleId] = useState(null);
    const [editingRuleData, setEditingRuleData] = useState(null);
    const [editingTablePos, setEditingTablePos] = useState(null);
    const [editingRowPos, setEditingRowPos] = useState(null);
    const [editingCellPos, setEditingCellPos] = useState(null);
    const [editingColIndex, setEditingColIndex] = useState(null);
    const [tableTargetOptions, setTableTargetOptions] = useState(null);
    const [editingSigPos, setEditingSigPos] = useState(null);

    const closeRuleModal = useCallback(() => {
        setIsRuleModalOpen(false);
        setEditingRuleId(null);
        setEditingRuleData(null);
        setEditingTablePos(null);
        setEditingRowPos(null);
        setEditingCellPos(null);
        setEditingColIndex(null);
        setEditingSigPos(null);
        setTableTargetOptions(null);
    }, []);
    const [editorZoom, setEditorZoom] = useState(100);
    const zoomAnchorRef = useRef(null);
    const lastMousePosRef = useRef({ x: 0, y: 0, isOver: false });
    const [isEditorSearchOpen, setIsEditorSearchOpen] = useState(true);
    const [editorSearchTerm, setEditorSearchTerm] = useState('');
    const [editorSearchResults, setEditorSearchResults] = useState([]);
    const [currentEditorMatchIndex, setCurrentEditorMatchIndex] = useState(0);
    const editorSearchInputRef = useRef(null);
    const currentEditorMatchIndexRef = useRef(0);
    const editorSearchResultsRef = useRef([]);
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                paragraph: false,
                italic: false,
                bulletList: false,
                horizontalRule: false,
                orderedList: false,
                listItem: {
                    keepMarks: true,
                },
            }),
            CustomParagraph,
            CustomOrderedList.configure({
                keepMarks: true,
                keepAttributes: true,
            }),
            CustomBulletList.configure({
                keepMarks: true,
                keepAttributes: true,
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph', 'tableCell', 'tableHeader', 'listItem'],
                defaultAlignment: null,
            }),
            Placeholder.configure({
                placeholder: 'Start typing your notice template here, or drag data fields from the sidebar...',
            }),
            CustomTable.configure({ resizable: true }),
            CustomTableRow,
            CustomTableHeader,
            CustomTableCell,
            Image,
            VariableTag,
            ConditionalBlock,
            OptionalBlock,
            ClearSpaceBlock,
            DollarBlock,
            PageBreakBlock,
            LineBreakBlock,
            HorizontalLineBlock,
            WhiteBlankImageBlock,
            SyntaxAutoSync,
            SmartSyntaxExtension,
            Mention.configure({
                HTMLAttributes: {
                    class: 'mention',
                },
                suggestion: getSuggestion(() => variablesRef.current),
            }),
            SearchHighlightExtension,
        ],
        content: initialContent || '',
        onCreate({ editor: editorInstance }) {
            onEditorReady(editorInstance);
            if (onVariablesExtracted) {
                const extracted = [];
                editorInstance.state.doc.descendants((node) => {
                    if (node.type.name === 'variableTag') {
                        const label = node.attrs.label || node.attrs.variableKey;
                        if (label && typeof label === 'string') {
                            const clean = label.replace(/<[^>]+>/g, '').replace(/(?:^<<|>>$)/g, '').trim();
                            if (clean && !extracted.includes(clean)) {
                                extracted.push(clean);
                            }
                        }
                    }
                });
                if (extracted.length > 0) {
                    onVariablesExtracted(extracted, { isManual: false });
                }
            }
        },
        onUpdate({ editor: editorInstance }) {
            onEditorReady(editorInstance);
            if (onContentChange) {
                onContentChange(editorInstance);
            }
            if (onVariablesExtracted) {
                const extracted = [];
                editorInstance.state.doc.descendants((node) => {
                    if (node.type.name === 'variableTag') {
                        const label = node.attrs.label || node.attrs.variableKey;
                        if (label && typeof label === 'string') {
                            const clean = label.replace(/<[^>]+>/g, '').replace(/(?:^<<|>>$)/g, '').trim();
                            if (clean && !extracted.includes(clean)) {
                                extracted.push(clean);
                            }
                        }
                    }
                });
                if (extracted.length > 0) {
                    onVariablesExtracted(extracted, { isManual: true });
                }
            }
        },
        editorProps: {
            handleDOMEvents: {
                dragover: (_view, event) => {
                    const dragged = getDraggedVariable();
                    const hasNoticeVar = event.dataTransfer?.types
                        ? Array.from(event.dataTransfer.types).includes('application/notice-variable')
                        : false;
                    if (dragged || hasNoticeVar) {
                        event.preventDefault();
                        if (event.dataTransfer) {
                            event.dataTransfer.dropEffect = dragged?.isInternal ? 'move' : 'copy';
                        }
                    }
                    return false;
                },
                dragenter: (_view, event) => {
                    const dragged = getDraggedVariable();
                    const hasNoticeVar = event.dataTransfer?.types
                        ? Array.from(event.dataTransfer.types).includes('application/notice-variable')
                        : false;
                    if (dragged || hasNoticeVar) {
                        event.preventDefault();
                    }
                    return false;
                },
            },
            handlePaste: (_view, event) => {
                const text = event.clipboardData?.getData('text/plain');
                if (text && onVariablesExtracted) {
                    const regex = /<<([^<]+)>>/g;
                    let match;
                    const extracted = [];
                    const seen = new Set();
                    while ((match = regex.exec(text)) !== null) {
                        const clean = match[1].replace(/<[^>]+>/g, '').replace(/\*\*/g, '').trim();
                        const norm = clean.toLowerCase();
                        if (clean && !seen.has(norm) && !clean.includes('||') && !clean.includes('==') && !clean.includes('<') && !clean.includes('>') && clean.length < 100) {
                            seen.add(norm);
                            extracted.push(clean);
                        }
                    }
                    if (extracted.length > 0) {
                        onVariablesExtracted(extracted, { isManual: true });
                    }
                }
                return false;
            },
            handleDrop: (view, event, slice, moved) => {
                document.body.classList.remove('is-dragging-variable');
                let varData = getDraggedVariable();
                if (!varData) {
                    const raw = event.dataTransfer?.getData('application/notice-variable');
                    if (raw) {
                        try {
                            varData = JSON.parse(raw);
                        }
                        catch {
                            // ignore
                        }
                    }
                }
                if (!varData && slice && slice.content && slice.content.firstChild) {
                    let vNode = slice.content.firstChild;
                    if (vNode.type.name === 'paragraph' && vNode.firstChild && vNode.firstChild.type.name === 'variableTag') {
                        vNode = vNode.firstChild;
                    }
                    if (vNode.type.name === 'variableTag') {
                        varData = {
                            key: vNode.attrs.variableKey || vNode.attrs.label,
                            label: vNode.attrs.label,
                            isInternal: true,
                            marks: (vNode.marks && Array.isArray(vNode.marks))
                                ? vNode.marks.map((m) => ({ type: m.type.name, attrs: m.attrs }))
                                : [],
                        };
                    }
                }
                if (!varData)
                    return false;
                event.preventDefault();
                const { key, label, fromPos, isInternal, marks } = varData;

                // Find the drop position in the document
                let targetPos = null;
                const coordinates = view.posAtCoords({
                    left: event.clientX,
                    top: event.clientY,
                });
                if (coordinates && typeof coordinates.pos === 'number') {
                    targetPos = coordinates.pos;
                } else {
                    const editorRect = view.dom.getBoundingClientRect();
                    if (event.clientY >= editorRect.bottom - 30) {
                        targetPos = view.state.doc.content.size;
                    } else if (event.clientY <= editorRect.top + 30) {
                        targetPos = 0;
                    } else if (event.target && view.dom.contains(event.target)) {
                        try {
                            targetPos = view.posAtDOM(event.target, 0);
                        } catch {
                            targetPos = view.state.selection.from;
                        }
                    } else {
                        targetPos = view.state.selection.from;
                    }
                }

                let tr = view.state.tr;

                // If dragging an internal variable, delete the source node first
                if (isInternal) {
                    let sourcePos = fromPos;
                    let foundPos = null;
                    if (typeof sourcePos === 'number' && sourcePos >= 0 && sourcePos < view.state.doc.content.size) {
                        const nodeAtSource = view.state.doc.nodeAt(sourcePos);
                        if (nodeAtSource && nodeAtSource.type.name === 'variableTag') {
                            foundPos = sourcePos;
                        }
                    }
                    if (foundPos === null && typeof sourcePos === 'number') {
                        const scanStart = Math.max(0, sourcePos - 5);
                        const scanEnd = Math.min(view.state.doc.content.size, sourcePos + 5);
                        view.state.doc.nodesBetween(scanStart, scanEnd, (n, p) => {
                            if (foundPos === null && n.type.name === 'variableTag' && (n.attrs.label === label || n.attrs.variableKey === key)) {
                                foundPos = p;
                            }
                        });
                    }
                    if (foundPos === null && view.state.selection && view.state.selection.node && view.state.selection.node.type.name === 'variableTag') {
                        foundPos = view.state.selection.from;
                    }

                    if (foundPos !== null) {
                        const nodeAtSource = view.state.doc.nodeAt(foundPos);
                        const nodeSize = nodeAtSource ? nodeAtSource.nodeSize : 1;
                        // If dropped inside or right on the same node, do nothing
                        if (targetPos >= foundPos && targetPos <= foundPos + nodeSize) {
                            setDraggedVariable(null);
                            return true;
                        }
                        // Delete the original variable node from the document
                        tr = tr.delete(foundPos, foundPos + nodeSize);
                        // Map targetPos to account for deleted source range
                        targetPos = tr.mapping.map(targetPos);
                    }
                }

                // Adjust targetPos to nearest valid textblock position in tr.doc
                targetPos = findNearestTextblockPos(tr.doc, targetPos);
                targetPos = Math.max(0, Math.min(tr.doc.content.size, targetPos));
                let $pos = tr.doc.resolve(targetPos);

                // Build marks if any
                let markInstances = [];
                if (marks && Array.isArray(marks)) {
                    markInstances = marks.map((m) => {
                        const markType = view.state.schema.marks[m.type];
                        return markType ? markType.create(m.attrs) : null;
                    }).filter(Boolean);
                }

                // Insert variableTag
                const node = view.state.schema.nodes.variableTag.create({
                    variableKey: key,
                    label,
                }, null, markInstances);

                // If dropped outside of textblock, wrap in paragraph
                if (!$pos.parent.isTextblock) {
                    const pNode = view.state.schema.nodes.paragraph.create(null, [
                        node,
                        view.state.schema.text(' '),
                    ]);
                    tr = tr.insert(targetPos, pNode);
                    view.dispatch(tr);
                    view.focus();
                    setDraggedVariable(null);
                    return true;
                }

                // Check if preceding position requires 1-letter spacing
                let needsSpaceBefore = false;
                const textBefore = $pos.parent.textBetween(0, $pos.parentOffset, null, ' ');
                if (textBefore.length > 0) {
                    const lastChar = textBefore[textBefore.length - 1];
                    if (lastChar && !/\s/.test(lastChar)) {
                        needsSpaceBefore = true;
                    }
                }

                // Check if succeeding position requires 1-letter spacing
                let needsSpaceAfter = false;
                const textAfter = $pos.parent.textBetween($pos.parentOffset, $pos.parent.content.size, null, ' ');
                if (textAfter.length > 0) {
                    const firstChar = textAfter[0];
                    if (firstChar && !/\s/.test(firstChar) && !/^[,\.;:!?\)]/.test(firstChar)) {
                        needsSpaceAfter = true;
                    }
                }
                else {
                    // At end of block/paragraph, add trailing space so user can type immediately
                    needsSpaceAfter = true;
                }

                let currentPos = targetPos;
                if (needsSpaceBefore) {
                    tr.insertText(' ', currentPos);
                    currentPos += 1;
                }
                tr.insert(currentPos, node);
                currentPos += node.nodeSize;
                if (needsSpaceAfter) {
                    tr.insertText(' ', currentPos);
                    currentPos += 1;
                }

                // Move cursor right after the newly inserted variable
                const finalPos = currentPos;
                tr.setSelection(view.state.selection.constructor.near(tr.doc.resolve(finalPos)));

                view.dispatch(tr);
                view.focus();
                setDraggedVariable(null);
                return true;
            },
        },
    });

    function parseRawConditionToRuleData(rawCond) {
        if (!rawCond) return null;
        let clean = String(rawCond).replace(/^IF\s+/i, '').replace(/^Condition:\s*/i, '').replace(/^\[\s*/, '').replace(/\s*\]$/, '').trim();
        if (!clean) return null;

        const op = clean.includes('||') ? '||' : '&&';
        const parts = clean.split(op === '||' ? /\s*\|\|\s*/ : /\s*&&\s*/);
        const rules = [];

        parts.forEach(part => {
            part = part.trim();
            if (!part) return;
            const eqMatch = part.match(/(?:<<([^>]+)>>|([^=]+))\s*==\s*(.+)/);
            if (eqMatch) {
                const field = (eqMatch[1] || eqMatch[2] || '').trim();
                const val = (eqMatch[3] || '').replace(/^["']|["']$/g, '').trim();
                const lowerVal = val.toLowerCase();
                if (lowerVal === 'yes') {
                    rules.push({ field, variable: field, operator: 'yes', value: 'Yes' });
                } else if (lowerVal === 'no') {
                    rules.push({ field, variable: field, operator: 'no', value: 'No' });
                } else {
                    rules.push({ field, variable: field, operator: 'equals', value: val });
                }
            } else {
                const varMatch = part.match(/<<([^>]+)>>/) || [null, part];
                const field = (varMatch[1] || part).replace(/\s+EXISTS\b/gi, '').trim();
                rules.push({ field, variable: field, operator: 'exists', value: '' });
            }
        });

        return {
            operator: op,
            rules: rules.length > 0 ? rules : [{ field: '', operator: 'exists', value: '' }],
            label: rawCond,
        };
    }

    function getTableContext(state) {
        const selection = state.selection;
        const $pos = selection.$anchorCell || selection.$from;
        let tableNode = null;
        let tablePos = null;
        let rowNode = null;
        let rowPos = null;
        let cellNode = null;
        let cellPos = null;
        let rowIndex = 0;
        let colIndex = 0;

        for (let d = $pos.depth; d > 0; d--) {
            const n = $pos.node(d);
            if (!cellNode && (n.type.name === 'tableCell' || n.type.name === 'tableHeader')) {
                cellNode = n;
                cellPos = $pos.before(d);
            }
            if (!rowNode && n.type.name === 'tableRow') {
                rowNode = n;
                rowPos = $pos.before(d);
            }
            if (!tableNode && n.type.name === 'table') {
                tableNode = n;
                tablePos = $pos.before(d);
                break;
            }
        }

        if (!tableNode) return null;

        if (tableNode && rowNode) {
            let rIdx = 0;
            tableNode.forEach((child) => {
                if (child === rowNode || (rowPos !== null && tablePos + 1 + rIdx === rowPos)) {
                    rowIndex = rIdx;
                }
                rIdx++;
            });
        }

        if (rowNode && cellNode) {
            let cIdx = 0;
            rowNode.forEach((child) => {
                if (child === cellNode) {
                    colIndex = cIdx;
                }
                cIdx++;
            });
        }

        let colHeaderName = `Column ${colIndex + 1}`;
        try {
            const firstRow = tableNode.maybeChild(0);
            if (firstRow) {
                let curC = 0;
                firstRow.forEach((c) => {
                    if (curC === colIndex) {
                        const txt = (c.textContent || '').trim();
                        if (txt) {
                            colHeaderName = `Column ${colIndex + 1} (${txt.length > 15 ? txt.slice(0, 15) + '...' : txt})`;
                        }
                    }
                    curC++;
                });
            }
        } catch (e) {}

        return {
            tableNode,
            tablePos,
            rowNode,
            rowPos,
            rowIndex,
            cellNode,
            cellPos,
            colIndex,
            colHeaderName,
            rowLabel: 'Entire Row',
            colLabel: 'Particular Column',
        };
    }

    const handleOpenTableCondition = useCallback((preferredScope = 'column') => {
        if (!editor) return;
        const tableContext = getTableContext(editor.state);
        if (!tableContext) return;

        const { tableNode, tablePos, rowNode, rowPos, rowIndex, colIndex, cellPos, cellNode, rowLabel, colLabel } = tableContext;

        let rowRuleData = null;
        if (rowNode?.attrs?.rowConditionData) {
            try {
                rowRuleData = typeof rowNode.attrs.rowConditionData === 'string'
                    ? JSON.parse(rowNode.attrs.rowConditionData)
                    : rowNode.attrs.rowConditionData;
            } catch (e) {}
        } else if (rowNode?.attrs?.rowCondition) {
            rowRuleData = parseRawConditionToRuleData(rowNode.attrs.rowCondition);
        }

        let colRuleData = null;
        let colRuleId = null;
        if (cellNode) {
            const markType = editor.state.schema.marks.conditionalBlock;
            if (markType) {
                cellNode.descendants((child) => {
                    if (!colRuleData && child.isInline && child.marks) {
                        const m = child.marks.find(mk => mk.type === markType);
                        if (m) {
                            colRuleId = m.attrs?.ruleId;
                            colRuleData = {
                                operator: m.attrs?.operator || '&&',
                                rules: m.attrs?.rules || [],
                                label: m.attrs?.label || '',
                            };
                        }
                    }
                });
            }
        }
        if (!colRuleData && tableNode?.attrs?.columnConditions && tableNode.attrs.columnConditions[colIndex]) {
            const cr = tableNode.attrs.columnConditions[colIndex];
            colRuleData = {
                operator: cr.operator || '&&',
                rules: cr.rules || [],
                label: cr.label || '',
            };
        }

        let selectedTarget = preferredScope;
        if (preferredScope === 'row' && !rowRuleData && colRuleData) {
            selectedTarget = 'column';
        } else if (preferredScope === 'column' && !colRuleData && rowRuleData) {
            selectedTarget = 'row';
        }

        setEditingTablePos(tablePos);
        setEditingRowPos(rowPos);
        setEditingColIndex(colIndex);
        setEditingCellPos(cellPos);
        setEditingSigPos(null);
        setEditingRuleId(selectedTarget === 'column' ? (colRuleId || `col_${colIndex}`) : `row_${rowPos}`);
        setEditingRuleData(selectedTarget === 'column' ? colRuleData : rowRuleData);
        setTableTargetOptions({
            tablePos,
            rowPos,
            rowIndex,
            colIndex,
            cellPos,
            rowLabel,
            colLabel,
            selectedTarget,
            rowRuleData,
            colRuleData,
        });
        setIsRuleModalOpen(true);
    }, [editor]);

    const handleAddCondition = useCallback(() => {
        if (!editor)
            return;
        const tableContext = getTableContext(editor.state);
        const { from, to } = editor.state.selection;
        const hasTextSelection = from !== to && !editor.state.selection.$anchorCell;

        if (tableContext && !hasTextSelection) {
            handleOpenTableCondition('column');
            return;
        }

        const isSig = editor.isActive('whiteBlankImage') || editor.state.selection.node?.type.name === 'whiteBlankImage';
        if (isSig) {
            const sigNode = editor.state.selection.node?.type.name === 'whiteBlankImage' ? editor.state.selection.node : null;
            const attrs = sigNode ? sigNode.attrs : editor.getAttributes('whiteBlankImage');
            const pos = editor.state.selection.from;
            setEditingTablePos(null);
            setEditingRowPos(null);
            setEditingColIndex(null);
            setTableTargetOptions(null);
            setEditingSigPos(pos);
            const currentRuleId = attrs.ruleId || `sig_${pos}`;
            setEditingRuleId(currentRuleId);
            let ruleData = null;
            if (attrs.conditionData) {
                try {
                    ruleData = typeof attrs.conditionData === 'string' ? JSON.parse(attrs.conditionData) : attrs.conditionData;
                } catch (e) {}
            } else if (attrs.condition) {
                ruleData = {
                    operator: '&&',
                    rules: [{ field: attrs.condition.replace(/<<|>>/g, '').split('==')[0].trim(), operator: 'equals', value: (attrs.condition.split('==')[1] || '').trim() }],
                    label: attrs.condition,
                };
            }
            setEditingRuleData(ruleData);
            setIsRuleModalOpen(true);
            return;
        }

        if (editor.isActive('conditionalBlock')) {
            const attrs = editor.getAttributes('conditionalBlock');
            setEditingTablePos(null);
            setEditingRowPos(null);
            setEditingColIndex(null);
            setTableTargetOptions(null);
            setEditingRuleId(attrs.ruleId);
            setEditingRuleData({
                operator: attrs.operator || 'AND',
                rules: attrs.rules || [],
            });
            setIsRuleModalOpen(true);
            return;
        }
        if (from === to) {
            alert('Please select the text or table you want to make conditional, then click "Add Condition".');
            return;
        }
        setEditingTablePos(null);
        setEditingRowPos(null);
        setEditingColIndex(null);
        setTableTargetOptions(null);
        setEditingRuleId(null);
        setEditingRuleData(null);
        setIsRuleModalOpen(true);
    }, [editor, handleOpenTableCondition]);
    // Always sync the editor to the parent whenever the useEditor hook updates it
    useEffect(() => {
        if (editor) {
            onEditorReady(editor);
        }
    }, [editor, onEditorReady]);
    const lastAppliedContentRef = useRef(initialContent);
    // Sync content when active template changes
    useEffect(() => {
        if (editor && initialContent !== undefined) {
            if (lastAppliedContentRef.current !== initialContent) {
                lastAppliedContentRef.current = initialContent;
                setTimeout(() => {
                    if (editor && !editor.isDestroyed) {
                        editor.commands.setContent(initialContent || '');
                    }
                }, 0);
            }
        }
    }, [editor, initialContent]);
    useEffect(() => {
        const handleGlobalDragEnd = () => {
            document.body.classList.remove('is-dragging-variable');
            setDraggedVariable(null);
        };
        window.addEventListener('dragend', handleGlobalDragEnd);
        window.addEventListener('mouseup', handleGlobalDragEnd);
        return () => {
            window.removeEventListener('dragend', handleGlobalDragEnd);
            window.removeEventListener('mouseup', handleGlobalDragEnd);
        };
    }, []);
    useEffect(() => {
        const handleEditEvent = (e) => {
            if (e.detail?.conditionType === 'row' || e.detail?.rowPos !== undefined || (e.detail?.ruleId && String(e.detail.ruleId).startsWith('row_'))) {
                setEditingRowPos(e.detail.rowPos);
                setEditingTablePos(e.detail.tablePos || null);
                setEditingColIndex(null);
                setEditingCellPos(null);
                setEditingSigPos(null);
                setEditingRuleId(e.detail.ruleId || `row_${e.detail.rowPos}`);
                setEditingRuleData(e.detail.data);
                setTableTargetOptions({
                    rowPos: e.detail.rowPos,
                    rowIndex: e.detail.rowIndex,
                    tablePos: e.detail.tablePos,
                    rowLabel: 'Entire Row',
                    colLabel: 'Particular Column',
                    selectedTarget: 'row',
                    rowRuleData: e.detail.data,
                    colRuleData: null,
                });
                setIsRuleModalOpen(true);
                return;
            }
            if (e.detail?.conditionType === 'column' || e.detail?.colIndex !== undefined || (e.detail?.ruleId && String(e.detail.ruleId).startsWith('col_'))) {
                setEditingColIndex(e.detail.colIndex);
                setEditingTablePos(e.detail.tablePos || null);
                setEditingRowPos(null);
                setEditingCellPos(e.detail.cellPos || null);
                setEditingSigPos(null);
                setEditingRuleId(e.detail.ruleId || `col_${e.detail.colIndex}`);
                setEditingRuleData(e.detail.data);
                setTableTargetOptions({
                    colIndex: e.detail.colIndex,
                    tablePos: e.detail.tablePos,
                    cellPos: e.detail.cellPos || null,
                    rowLabel: 'Entire Row',
                    colLabel: 'Particular Column',
                    selectedTarget: 'column',
                    rowRuleData: null,
                    colRuleData: e.detail.data,
                });
                setIsRuleModalOpen(true);
                return;
            }
            if (e.detail?.tablePos !== undefined || (e.detail?.ruleId && String(e.detail.ruleId).startsWith('table_'))) {
                setEditingTablePos(e.detail.tablePos);
                setEditingRowPos(null);
                setEditingColIndex(null);
                setTableTargetOptions(null);
                setEditingSigPos(null);
                setEditingRuleId(e.detail.ruleId || `table_${e.detail.tablePos}`);
                setEditingRuleData(e.detail.data);
                setIsRuleModalOpen(true);
                return;
            }
            if (e.detail?.sigPos !== undefined || (e.detail?.ruleId && String(e.detail.ruleId).startsWith('sig_'))) {
                setEditingSigPos(e.detail.sigPos);
                setEditingTablePos(null);
                setEditingRowPos(null);
                setEditingColIndex(null);
                setTableTargetOptions(null);
                setEditingRuleId(e.detail.ruleId || `sig_${e.detail.sigPos}`);
                setEditingRuleData(e.detail.data);
                setIsRuleModalOpen(true);
                return;
            }
            setEditingTablePos(null);
            setEditingRowPos(null);
            setEditingColIndex(null);
            setTableTargetOptions(null);
            setEditingSigPos(null);
            setEditingRuleId(e.detail.ruleId);
            setEditingRuleData(e.detail.data);
            setIsRuleModalOpen(true);
        };
        window.addEventListener('openConditionEdit', handleEditEvent);
        return () => window.removeEventListener('openConditionEdit', handleEditEvent);
    }, []);
    function removeMarkFromEnclosingOrSelectedRange(targetEditor, markName) {
        if (!targetEditor) return false;
        const { state, view } = targetEditor;
        const { doc, selection } = state;
        const { from, to, empty } = selection;
        const markType = state.schema.marks[markName];
        if (!markType) return false;

        let markRanges = [];
        const $from = doc.resolve(from);
        const $to = doc.resolve(to);
        const scanStart = Math.max(0, $from.start() - 200);
        const scanEnd = Math.min(doc.content.size, $to.end() + 200);

        let currentRange = null;
        doc.nodesBetween(scanStart, scanEnd, (node, pos) => {
            if (node.isInline) {
                const hasMark = node.marks?.some(m => m.type === markType);
                const nodeStart = pos;
                const nodeEnd = pos + node.nodeSize;
                if (hasMark) {
                    if (currentRange && currentRange.to === nodeStart) {
                        currentRange.to = nodeEnd;
                    } else {
                        if (currentRange) markRanges.push(currentRange);
                        currentRange = { from: nodeStart, to: nodeEnd };
                    }
                } else {
                    if (currentRange) {
                        markRanges.push(currentRange);
                        currentRange = null;
                    }
                }
            }
        });
        if (currentRange) markRanges.push(currentRange);

        const intersectingRanges = markRanges.filter(r => {
            if (empty) {
                return from >= r.from && from <= r.to;
            }
            return Math.max(from, r.from) <= Math.min(to, r.to) || (from <= r.from && to >= r.to);
        });

        if (intersectingRanges.length > 0) {
            const tr = state.tr;
            intersectingRanges.forEach(r => {
                tr.removeMark(r.from, r.to, markType);
            });
            view.dispatch(tr);
            return true;
        }

        if (targetEditor.isActive(markName)) {
            targetEditor.chain().focus().unsetMark(markName).run();
            return true;
        }

        return false;
    }

    const handleAddOptional = useCallback(() => {
        if (!editor)
            return;
        const removed = removeMarkFromEnclosingOrSelectedRange(editor, 'optionalBlock');
        if (removed) {
            return;
        }
        const { from, to } = editor.state.selection;
        if (from === to) {
            alert('Please select the text you want to make optional, then click "Make Optional".');
            return;
        }
        editor.chain().focus().setMark('optionalBlock').run();
    }, [editor]);
    const handleAddDollar = useCallback(() => {
        if (!editor)
            return;
        const removed = removeMarkFromEnclosingOrSelectedRange(editor, 'dollarBlock');
        if (removed) {
            return;
        }
        const { from, to } = editor.state.selection;
        if (from === to) {
            alert('Please select the variable/text you want to mark with $, then click "Clear Row ($)".');
            return;
        }
        editor.chain().focus().setMark('dollarBlock').run();
    }, [editor]);
    const handleAddClearSpace = useCallback(() => {
        if (!editor)
            return;
        const removed = removeMarkFromEnclosingOrSelectedRange(editor, 'clearSpaceBlock');
        if (removed) {
            return;
        }
        const { from, to } = editor.state.selection;
        if (from === to) {
            alert('Please select the text you want to apply the Clear Space (@) rule to, then click "Clear Space @".');
            return;
        }
        editor.chain().focus().setMark('clearSpaceBlock').run();
    }, [editor]);
    const handleAddImage = useCallback(() => {
        if (!editor)
            return;
        // Create a hidden file input to trigger upload
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const target = e.target;
            if (target.files && target.files.length > 0) {
                const file = target.files[0];
                const reader = new FileReader();
                reader.onload = (event) => {
                    const dataUrl = event.target?.result;
                    if (dataUrl) {
                        editor.chain().focus().setImage({ src: dataUrl }).run();
                    }
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    }, [editor]);
    const handleSaveRule = useCallback((rule) => {
        if (!editor)
            return;

        const targetScope = rule.targetScope || (editingCellPos !== null ? 'column' : (editingRowPos !== null ? 'row' : (editingColIndex !== null ? 'column' : null)));

        if (targetScope === 'row' && (editingRowPos !== null || tableTargetOptions?.rowPos || (editingRuleId && String(editingRuleId).startsWith('row_')))) {
            let targetPos = editingRowPos || tableTargetOptions?.rowPos;
            if (targetPos === null || targetPos === undefined) {
                const ctx = getTableContext(editor.state);
                if (ctx && ctx.rowPos !== null) targetPos = ctx.rowPos;
            }
            if (targetPos !== null && targetPos !== undefined) {
                const tr = editor.state.tr;
                let foundRow = false;
                editor.state.doc.descendants((node, pos) => {
                    if (!foundRow && node.type.name === 'tableRow') {
                        if (pos === targetPos || (targetPos >= pos && targetPos < pos + node.nodeSize)) {
                            tr.setNodeMarkup(pos, null, {
                                ...node.attrs,
                                rowCondition: rule.label,
                                rowConditionData: JSON.stringify(rule),
                            });
                            foundRow = true;
                            return false;
                        }
                    }
                });
                if (foundRow) {
                    editor.view.dispatch(tr);
                    closeRuleModal();
                    return;
                }
            }
        }

        if (targetScope === 'column') {
            let targetCellPos = editingCellPos || tableTargetOptions?.cellPos;
            let targetCellNode = null;

            if (targetCellPos !== null && targetCellPos !== undefined) {
                const n = editor.state.doc.nodeAt(targetCellPos);
                if (n && (n.type.name === 'tableCell' || n.type.name === 'tableHeader')) {
                    targetCellNode = n;
                }
            }

            if (!targetCellNode) {
                const ctx = getTableContext(editor.state);
                if (ctx && ctx.cellNode && ctx.cellPos !== null) {
                    targetCellPos = ctx.cellPos;
                    targetCellNode = ctx.cellNode;
                }
            }

            if (!targetCellNode) {
                const rowPosToSearch = editingRowPos || tableTargetOptions?.rowPos;
                const colIdxToSearch = editingColIndex !== null ? editingColIndex : tableTargetOptions?.colIndex;
                if (rowPosToSearch !== null && colIdxToSearch !== null && colIdxToSearch !== undefined) {
                    editor.state.doc.descendants((node, pos) => {
                        if (!targetCellNode && node.type.name === 'tableRow') {
                            if (pos === rowPosToSearch || (rowPosToSearch >= pos && rowPosToSearch < pos + node.nodeSize)) {
                                let cIdx = 0;
                                let cOffset = pos + 1;
                                node.forEach((child) => {
                                    if (cIdx === colIdxToSearch && !targetCellNode) {
                                        targetCellPos = cOffset;
                                        targetCellNode = child;
                                    }
                                    cOffset += child.nodeSize;
                                    cIdx++;
                                });
                                return false;
                            }
                        }
                    });
                }
            }

            if (targetCellNode && targetCellPos !== null) {
                const tr = editor.state.tr;
                const markType = editor.state.schema.marks.conditionalBlock;
                const cellRuleId = `cell_cond_${Date.now()}`;
                const newMark = markType.create({
                    ruleId: cellRuleId,
                    operator: rule.operator,
                    rules: rule.rules,
                    label: rule.label,
                });

                let hasInline = false;
                tr.doc.nodesBetween(targetCellPos, targetCellPos + targetCellNode.nodeSize, (inlineNode, inlinePos) => {
                    if (inlineNode.isInline) {
                        tr.removeMark(inlinePos, inlinePos + inlineNode.nodeSize, markType);
                        tr.addMark(inlinePos, inlinePos + inlineNode.nodeSize, newMark);
                        hasInline = true;
                    }
                });

                if (!hasInline) {
                    let insertPos = targetCellPos + 2;
                    targetCellNode.forEach((c, offset) => {
                        if (!hasInline && c.type.name === 'paragraph') {
                            insertPos = targetCellPos + 1 + offset + 1;
                        }
                    });
                    tr.insert(insertPos, editor.state.schema.text(' ', [newMark]));
                }

                editor.view.dispatch(tr);
                closeRuleModal();
                return;
            }
        }

        if (editingTablePos !== null || (editingRuleId && String(editingRuleId).startsWith('table_'))) {
            const targetPos = editingTablePos;
            const tr = editor.state.tr;
            let foundTable = false;
            editor.state.doc.descendants((node, pos) => {
                if (node.type.name === 'table') {
                    if (targetPos === null || pos === targetPos || (targetPos >= pos && targetPos < pos + node.nodeSize)) {
                        tr.setNodeMarkup(pos, null, {
                            ...node.attrs,
                            tableCondition: rule.label,
                            tableConditionData: JSON.stringify(rule),
                        });
                        const markType = editor.state.schema.marks.conditionalBlock;
                        if (markType) {
                            node.descendants((child, childPos) => {
                                if (child.isInline && child.marks?.some(m => m.type === markType)) {
                                    tr.removeMark(pos + 1 + childPos, pos + 1 + childPos + child.nodeSize, markType);
                                }
                            });
                        }
                        foundTable = true;
                        return false;
                    }
                }
            });
            if (foundTable) {
                editor.view.dispatch(tr);
                closeRuleModal();
                return;
            }
        }
        if (editingSigPos !== null || (editingRuleId && String(editingRuleId).startsWith('sig_'))) {
            const targetPos = editingSigPos;
            const tr = editor.state.tr;
            let foundSig = false;
            editor.state.doc.descendants((node, pos) => {
                if (node.type.name === 'whiteBlankImage') {
                    if (targetPos === null || pos === targetPos || (targetPos >= pos && targetPos < pos + node.nodeSize) || node.attrs?.ruleId === editingRuleId) {
                        tr.setNodeMarkup(pos, null, {
                            ...node.attrs,
                            condition: rule.label,
                            conditionData: JSON.stringify(rule),
                            ruleId: editingRuleId || `sig_${Date.now()}`,
                        });
                        foundSig = true;
                        return false;
                    }
                }
            });
            if (foundSig) {
                editor.view.dispatch(tr);
                closeRuleModal();
                return;
            }
        }
        const ruleId = editingRuleId || `rule_${Date.now()}`;
        if (editingRuleId) {
            const markType = editor.state.schema.marks.conditionalBlock;
            if (markType) {
                const tr = editor.state.tr;
                let updated = false;
                editor.state.doc.descendants((node, pos) => {
                    if (node.isInline) {
                        const mark = node.marks?.find(m => m.type === markType && m.attrs?.ruleId === editingRuleId);
                        if (mark) {
                            tr.removeMark(pos, pos + node.nodeSize, markType);
                            tr.addMark(pos, pos + node.nodeSize, markType.create({
                                ruleId,
                                operator: rule.operator,
                                rules: rule.rules,
                                label: rule.label,
                            }));
                            updated = true;
                        }
                    }
                });
                if (updated) {
                    editor.view.dispatch(tr);
                    closeRuleModal();
                    return;
                }
            }
        }
        // Apply the conditionalBlock mark to the selection
        editor
            .chain()
            .focus()
            .setMark('conditionalBlock', {
            ruleId,
            operator: rule.operator,
            rules: rule.rules,
            label: rule.label,
        })
            .run();
        closeRuleModal();
    }, [editor, editingRuleId, editingTablePos, editingRowPos, editingColIndex, editingCellPos, editingSigPos, tableTargetOptions, closeRuleModal]);
    const handleRemoveRule = useCallback((removeData) => {
        if (!editor)
            return;

        const targetScope = removeData?.targetScope || (editingCellPos !== null ? 'column' : (editingRowPos !== null ? 'row' : (editingColIndex !== null ? 'column' : null)));

        if (targetScope === 'row' && (editingRowPos !== null || tableTargetOptions?.rowPos)) {
            let targetPos = editingRowPos || tableTargetOptions?.rowPos;
            if (targetPos === null || targetPos === undefined) {
                const ctx = getTableContext(editor.state);
                if (ctx && ctx.rowPos !== null) targetPos = ctx.rowPos;
            }
            if (targetPos !== null && targetPos !== undefined) {
                const tr = editor.state.tr;
                let foundRow = false;
                editor.state.doc.descendants((node, pos) => {
                    if (!foundRow && node.type.name === 'tableRow') {
                        if (pos === targetPos || (targetPos >= pos && targetPos < pos + node.nodeSize)) {
                            const newAttrs = { ...node.attrs };
                            newAttrs.rowCondition = null;
                            newAttrs.rowConditionData = null;
                            delete newAttrs.rowCondition;
                            delete newAttrs.rowConditionData;
                            tr.setNodeMarkup(pos, null, newAttrs);
                            foundRow = true;
                            return false;
                        }
                    }
                });
                if (foundRow) {
                    editor.view.dispatch(tr);
                    closeRuleModal();
                    return;
                }
            }
        }

        if (targetScope === 'column') {
            let targetCellPos = editingCellPos || tableTargetOptions?.cellPos;
            let targetCellNode = null;
            if (targetCellPos !== null && targetCellPos !== undefined) {
                const n = editor.state.doc.nodeAt(targetCellPos);
                if (n && (n.type.name === 'tableCell' || n.type.name === 'tableHeader')) {
                    targetCellNode = n;
                }
            }
            if (!targetCellNode) {
                const ctx = getTableContext(editor.state);
                if (ctx && ctx.cellNode && ctx.cellPos !== null) {
                    targetCellPos = ctx.cellPos;
                    targetCellNode = ctx.cellNode;
                }
            }
            if (!targetCellNode) {
                const rowPosToSearch = editingRowPos || tableTargetOptions?.rowPos;
                const colIdxToSearch = editingColIndex !== null ? editingColIndex : tableTargetOptions?.colIndex;
                if (rowPosToSearch !== null && colIdxToSearch !== null && colIdxToSearch !== undefined) {
                    editor.state.doc.descendants((node, pos) => {
                        if (!targetCellNode && node.type.name === 'tableRow') {
                            if (pos === rowPosToSearch || (rowPosToSearch >= pos && rowPosToSearch < pos + node.nodeSize)) {
                                let cIdx = 0;
                                let cOffset = pos + 1;
                                node.forEach((child) => {
                                    if (cIdx === colIdxToSearch && !targetCellNode) {
                                        targetCellPos = cOffset;
                                        targetCellNode = child;
                                    }
                                    cOffset += child.nodeSize;
                                    cIdx++;
                                });
                                return false;
                            }
                        }
                    });
                }
            }
            if (targetCellNode && targetCellPos !== null) {
                const tr = editor.state.tr;
                const markType = editor.state.schema.marks.conditionalBlock;
                if (markType) {
                    tr.doc.nodesBetween(targetCellPos, targetCellPos + targetCellNode.nodeSize, (inlineNode, inlinePos) => {
                        if (inlineNode.isInline) {
                            tr.removeMark(inlinePos, inlinePos + inlineNode.nodeSize, markType);
                        }
                    });
                }
                editor.view.dispatch(tr);
                closeRuleModal();
                return;
            }
        }

        if (editingTablePos !== null || (editingRuleId && String(editingRuleId).startsWith('table_'))) {
            const targetPos = editingTablePos;
            const tr = editor.state.tr;
            let foundTable = false;
            editor.state.doc.descendants((node, pos) => {
                if (node.type.name === 'table') {
                    if (targetPos === null || pos === targetPos || (targetPos >= pos && targetPos < pos + node.nodeSize)) {
                        const newAttrs = { ...node.attrs };
                        newAttrs.tableCondition = null;
                        newAttrs.tableConditionData = null;
                        delete newAttrs.tableCondition;
                        delete newAttrs.tableConditionData;
                        tr.setNodeMarkup(pos, null, newAttrs);
                        foundTable = true;
                        return false;
                    }
                }
            });
            if (foundTable) {
                editor.view.dispatch(tr);
                closeRuleModal();
                return;
            }
        }
        if (editingSigPos !== null || (editingRuleId && String(editingRuleId).startsWith('sig_'))) {
            const targetPos = editingSigPos;
            const tr = editor.state.tr;
            let foundSig = false;
            editor.state.doc.descendants((node, pos) => {
                if (node.type.name === 'whiteBlankImage') {
                    if (targetPos === null || pos === targetPos || (targetPos >= pos && targetPos < pos + node.nodeSize) || node.attrs?.ruleId === editingRuleId) {
                        const newAttrs = { ...node.attrs };
                        newAttrs.condition = '';
                        newAttrs.conditionData = '';
                        newAttrs.ruleId = '';
                        delete newAttrs.condition;
                        delete newAttrs.conditionData;
                        delete newAttrs.ruleId;
                        tr.setNodeMarkup(pos, null, newAttrs);
                        foundSig = true;
                        return false;
                    }
                }
            });
            if (foundSig) {
                editor.view.dispatch(tr);
                closeRuleModal();
                return;
            }
        }
        const markType = editor.state.schema.marks.conditionalBlock;
        if (markType) {
            const tr = editor.state.tr;
            let removedAny = false;
            if (editingRuleId) {
                editor.state.doc.descendants((node, pos) => {
                    if (node.isInline) {
                        const mark = node.marks?.find(m => m.type === markType && (m.attrs?.ruleId === editingRuleId || !editingRuleId));
                        if (mark) {
                            tr.removeMark(pos, pos + node.nodeSize, markType);
                            removedAny = true;
                        }
                    }
                });
            }
            if (removedAny) {
                editor.view.dispatch(tr);
            } else {
                removeMarkFromEnclosingOrSelectedRange(editor, 'conditionalBlock');
            }
        }
        closeRuleModal();
    }, [editor, editingRuleId, editingTablePos, editingRowPos, editingColIndex, editingCellPos, editingSigPos, tableTargetOptions, closeRuleModal]);
    
    const handleNextEditorMatchRef = useRef();
    const handlePrevEditorMatchRef = useRef();

    const runEditorSearch = useCallback((term, targetIndex = 0) => {
        if (!editor || editor.isDestroyed) return;
        const query = (term || '').trim().toLowerCase();
        if (!query) {
            editorSearchResultsRef.current = [];
            setEditorSearchResults([]);
            currentEditorMatchIndexRef.current = 0;
            setCurrentEditorMatchIndex(0);
            try {
                const tr = editor.state.tr.setMeta(searchHighlightPluginKey, DecorationSet.empty);
                editor.view.dispatch(tr);
            } catch (err) {
                // ignore
            }
            return;
        }

        const results = [];
        editor.state.doc.descendants((node, pos) => {
            if (node.isText && node.text) {
                const text = node.text.toLowerCase();
                let idx = text.indexOf(query);
                while (idx !== -1) {
                    results.push({
                        from: pos + idx,
                        to: pos + idx + query.length,
                        isNode: false,
                    });
                    idx = text.indexOf(query, idx + query.length);
                }
            } else if (node.type.name === 'variableTag') {
                const text = (node.attrs.label || node.attrs.variableKey || '').toLowerCase();
                if (text.includes(query)) {
                    results.push({
                        from: pos,
                        to: pos + node.nodeSize,
                        isNode: true,
                        nodeType: 'variableTag',
                    });
                }
            } else if (node.type.name === 'whiteBlankImage') {
                const text = ((node.attrs.filename || '') + ' ' + (node.attrs.condition || '')).toLowerCase();
                if (text.includes(query)) {
                    results.push({
                        from: pos,
                        to: pos + node.nodeSize,
                        isNode: true,
                        nodeType: 'whiteBlankImage',
                    });
                }
            }
        });

        editorSearchResultsRef.current = results;
        setEditorSearchResults(results);
        if (results.length === 0) {
            currentEditorMatchIndexRef.current = 0;
            setCurrentEditorMatchIndex(0);
            try {
                const tr = editor.state.tr.setMeta(searchHighlightPluginKey, DecorationSet.empty);
                editor.view.dispatch(tr);
            } catch (err) {
                // ignore
            }
            return;
        }

        const activeIdx = Math.max(0, Math.min(targetIndex, results.length - 1));
        currentEditorMatchIndexRef.current = activeIdx;
        setCurrentEditorMatchIndex(activeIdx);

        try {
            const decorations = results.map((r, idx) => {
                const isActive = idx === activeIdx;
                if (r.isNode) {
                    return Decoration.node(r.from, r.to, {
                        class: isActive ? 'editor-search-match is-active' : 'editor-search-match',
                    });
                }
                return Decoration.inline(r.from, r.to, {
                    class: isActive ? 'editor-search-match is-active' : 'editor-search-match',
                });
            });

            const decSet = DecorationSet.create(editor.state.doc, decorations);
            const tr = editor.state.tr.setMeta(searchHighlightPluginKey, decSet);

            // Update editor selection to active match position so cursor follows traversal
            const activeResult = results[activeIdx];
            if (activeResult) {
                try {
                    if (activeResult.isNode) {
                        tr.setSelection(NodeSelection.create(editor.state.doc, activeResult.from));
                    } else {
                        tr.setSelection(TextSelection.create(editor.state.doc, activeResult.from, activeResult.to));
                    }
                } catch (selErr) {
                    // ignore selection failures on unusual positions
                }
            }

            editor.view.dispatch(tr);

            // Smoothly scroll active match into view inside editor-area container
            setTimeout(() => {
                const activeEl = document.querySelector('#editor-area .editor-search-match.is-active')
                    || document.querySelector('#editor-area .variable-tag-wrapper.editor-search-match.is-active')
                    || document.querySelector('#editor-area .ProseMirror-selectednode');
                if (activeEl) {
                    const container = document.getElementById('editor-area');
                    if (container) {
                        const containerRect = container.getBoundingClientRect();
                        const elRect = activeEl.getBoundingClientRect();
                        const relativeTop = elRect.top - containerRect.top + container.scrollTop;
                        const targetScrollTop = relativeTop - (container.clientHeight / 2) + (elRect.height / 2);
                        container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
                    }
                    activeEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                }
            }, 30);
        } catch (err) {
            console.error('Error applying search decorations:', err);
        }
    }, [editor]);

    const handleNextEditorMatch = useCallback(() => {
        const results = editorSearchResultsRef.current;
        if (!results || results.length === 0) return;
        const nextIdx = (currentEditorMatchIndexRef.current + 1) % results.length;
        const term = editorSearchTerm || editorSearchInputRef.current?.value || '';
        runEditorSearch(term, nextIdx);
    }, [editorSearchTerm, runEditorSearch]);
    handleNextEditorMatchRef.current = handleNextEditorMatch;

    const handlePrevEditorMatch = useCallback(() => {
        const results = editorSearchResultsRef.current;
        if (!results || results.length === 0) return;
        const prevIdx = (currentEditorMatchIndexRef.current - 1 + results.length) % results.length;
        const term = editorSearchTerm || editorSearchInputRef.current?.value || '';
        runEditorSearch(term, prevIdx);
    }, [editorSearchTerm, runEditorSearch]);
    handlePrevEditorMatchRef.current = handlePrevEditorMatch;

    const handleEditorSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (e.shiftKey) {
                handlePrevEditorMatch();
            } else {
                handleNextEditorMatch();
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            handleNextEditorMatch();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            handlePrevEditorMatch();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsEditorSearchOpen(false);
            if (editor) editor.commands.focus();
        }
    };

    useEffect(() => {
        runEditorSearch(editorSearchTerm, 0);
    }, [editorSearchTerm, runEditorSearch]);

    useEffect(() => {
        if (!editor) return;
        const handleDocUpdate = () => {
            if (editorSearchTerm.trim()) {
                runEditorSearch(editorSearchTerm, currentEditorMatchIndexRef.current || 0);
            }
        };
        editor.on('update', handleDocUpdate);
        return () => {
            editor.off('update', handleDocUpdate);
        };
    }, [editor, editorSearchTerm, runEditorSearch]);

    useEffect(() => {
        if (!isEditorSearchOpen && editor && !editor.isDestroyed) {
            setEditorSearchTerm('');
            setEditorSearchResults([]);
            try {
                const tr = editor.state.tr.setMeta(searchHighlightPluginKey, DecorationSet.empty);
                editor.view.dispatch(tr);
            } catch (err) {
                // ignore
            }
        }
    }, [isEditorSearchOpen, editor]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
                e.preventDefault();
                if (isEditorSearchOpen && editorSearchTerm.trim()) {
                    if (handleNextEditorMatchRef.current) handleNextEditorMatchRef.current();
                } else {
                    setIsEditorSearchOpen(true);
                    setTimeout(() => {
                        if (editorSearchInputRef.current) editorSearchInputRef.current.focus();
                        if (editorSearchInputRef.current) editorSearchInputRef.current.select();
                    }, 50);
                }
            } else if (e.key === 'F3' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g')) {
                e.preventDefault();
                if (e.shiftKey) {
                    if (handlePrevEditorMatchRef.current) handlePrevEditorMatchRef.current();
                } else {
                    if (handleNextEditorMatchRef.current) handleNextEditorMatchRef.current();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isEditorSearchOpen, editorSearchTerm]);

    const getCursorZoomAnchor = useCallback((explicitPoint = null) => {
        const container = document.getElementById('editor-area');
        if (!container) return null;
        const rect = container.getBoundingClientRect();

        // 1. Explicit point (e.g. from mouse wheel event)
        if (explicitPoint) {
            return {
                x: explicitPoint.x,
                y: explicitPoint.y,
                oldScrollTop: container.scrollTop,
                oldScrollLeft: container.scrollLeft,
                oldZoom: editorZoom,
            };
        }

        // 2. Mouse cursor is currently hovering inside the editor area
        if (lastMousePosRef.current?.isOver) {
            return {
                x: lastMousePosRef.current.x,
                y: lastMousePosRef.current.y,
                oldScrollTop: container.scrollTop,
                oldScrollLeft: container.scrollLeft,
                oldZoom: editorZoom,
            };
        }

        // 3. Text cursor (caret) inside editor if visible
        if (editor && editor.view) {
            try {
                const { selection } = editor.state;
                const coords = editor.view.coordsAtPos(selection.from);
                if (coords && coords.top >= rect.top - 100 && coords.bottom <= rect.bottom + 100) {
                    return {
                        x: coords.left - rect.left,
                        y: coords.top - rect.top,
                        oldScrollTop: container.scrollTop,
                        oldScrollLeft: container.scrollLeft,
                        oldZoom: editorZoom,
                    };
                }
            } catch (err) {
                // ignore
            }
        }

        // 4. Last known mouse coordinate if within container bounds
        if (lastMousePosRef.current && lastMousePosRef.current.x >= 0 && lastMousePosRef.current.x <= container.clientWidth) {
            return {
                x: lastMousePosRef.current.x,
                y: lastMousePosRef.current.y,
                oldScrollTop: container.scrollTop,
                oldScrollLeft: container.scrollLeft,
                oldZoom: editorZoom,
            };
        }

        // 5. Default to container viewport center
        return {
            x: container.clientWidth / 2,
            y: container.clientHeight / 2,
            oldScrollTop: container.scrollTop,
            oldScrollLeft: container.scrollLeft,
            oldZoom: editorZoom,
        };
    }, [editor, editorZoom]);

    const applyZoom = useCallback((newZoomVal, explicitPoint = null) => {
        const clampedZoom = Math.max(50, Math.min(200, newZoomVal));
        if (clampedZoom === editorZoom) return;

        const anchor = getCursorZoomAnchor(explicitPoint);
        zoomAnchorRef.current = anchor;
        setEditorZoom(clampedZoom);
    }, [editorZoom, getCursorZoomAnchor]);

    const handleZoomIn = () => applyZoom(editorZoom + 10);
    const handleZoomOut = () => applyZoom(editorZoom - 10);
    const handleResetZoom = () => applyZoom(100);

    // Adjust scroll so the cursor area remains pinned during zoom
    useEffect(() => {
        const anchor = zoomAnchorRef.current;
        if (!anchor) return;
        zoomAnchorRef.current = null;

        const container = document.getElementById('editor-area');
        if (!container) return;

        const ratio = (editorZoom / 100) / (anchor.oldZoom / 100);
        const newScrollTop = (anchor.oldScrollTop + anchor.y) * ratio - anchor.y;
        const newScrollLeft = (anchor.oldScrollLeft + anchor.x) * ratio - anchor.x;

        container.scrollTop = Math.max(0, newScrollTop);
        container.scrollLeft = Math.max(0, newScrollLeft);
    }, [editorZoom]);

    // Ctrl + Wheel zoom anchored to mouse cursor
    useEffect(() => {
        const container = document.getElementById('editor-area');
        if (!container) return;

        const handleWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const rect = container.getBoundingClientRect();
                const mousePoint = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                };
                if (e.deltaY < 0) {
                    applyZoom(editorZoom + 10, mousePoint);
                } else if (e.deltaY > 0) {
                    applyZoom(editorZoom - 10, mousePoint);
                }
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel);
    }, [editorZoom, applyZoom]);
    
    return (<div className="template-editor-container" id="template-editor-container">
      <EditorToolbar
        editor={editor}
        onAddCondition={handleAddCondition}
        onAddOptional={handleAddOptional}
        onAddClearSpace={handleAddClearSpace}
        onAddDollar={handleAddDollar}
        onOpenSyntaxHelp={() => setIsSyntaxHelpOpen(true)}
      />

      {/* Editor Search Bar */}
      {isEditorSearchOpen && (
        <div style={{
          padding: '8px 20px',
          backgroundColor: 'var(--accent-teal, #068989)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexShrink: 0,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.18)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '8px',
            padding: '5px 12px',
            gap: '8px',
            maxWidth: '380px',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <input
              ref={editorSearchInputRef}
              type="text"
              value={editorSearchTerm}
              onChange={(e) => setEditorSearchTerm(e.target.value)}
              onKeyDown={handleEditorSearchKeyDown}
              placeholder="Search document"
              className="search-doc-input"
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: '#ffffff',
                fontSize: '13px',
                width: '100%',
                fontFamily: 'inherit'
              }}
            />
            {editorSearchTerm && (
              <span style={{
                fontSize: '11px',
                color: '#ffffff',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                padding: '0 4px',
                userSelect: 'none'
              }}>
                {editorSearchResults.length > 0 ? `${currentEditorMatchIndex + 1}/${editorSearchResults.length}` : '0/0'}
              </span>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
              <button
                type="button"
                onClick={handlePrevEditorMatch}
                onMouseDown={(e) => e.preventDefault()}
                disabled={editorSearchResults.length === 0}
                title="Previous match (Shift+Enter)"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: editorSearchResults.length > 0 ? 'pointer' : 'default',
                  padding: '2px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: editorSearchResults.length > 0 ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
                  opacity: editorSearchResults.length > 0 ? 1 : 0.5,
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => { if (editorSearchResults.length > 0) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'; }}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={handleNextEditorMatch}
                onMouseDown={(e) => e.preventDefault()}
                disabled={editorSearchResults.length === 0}
                title="Next match (Enter)"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: editorSearchResults.length > 0 ? 'pointer' : 'default',
                  padding: '2px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: editorSearchResults.length > 0 ? '#ffffff' : 'rgba(255, 255, 255, 0.4)',
                  opacity: editorSearchResults.length > 0 ? 1 : 0.5,
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => { if (editorSearchResults.length > 0) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'; }}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <ChevronRight size={16} />
              </button>
            </div>
            {editorSearchTerm && (
              <button
                type="button"
                onClick={() => setEditorSearchTerm('')}
                title="Clear search"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#ffffff',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsEditorSearchOpen(false)}
            title="Close search (Esc)"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>
      )}

      <div className="editor-area" id="editor-area"
        onClick={(e) => {
          if (!editor) return;
          if (e.target.id === 'editor-area') {
            editor.commands.focus('end');
          }
        }}
        onMouseMove={(e) => {
          const container = e.currentTarget;
          const rect = container.getBoundingClientRect();
          lastMousePosRef.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            isOver: true,
          };
        }}
        onMouseLeave={() => {
          if (lastMousePosRef.current) {
            lastMousePosRef.current.isOver = false;
          }
        }}
        onDragOver={(e) => {
            if (e.dataTransfer.types.includes('application/notice-variable')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
            }
        }}>
        {editor && (<BubbleMenu editor={editor} shouldShow={({ editor: e }) => e.isActive('conditionalBlock')}>
            <div style={{ display: 'flex', background: '#fff', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '4px', gap: '4px' }}>
              <button onClick={handleAddCondition} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#4f46e5' }} title="Edit Condition">
                <Edit2 size={16}/>
              </button>
              <div style={{ width: '1px', background: '#e2e8f0', margin: '4px 0' }}/>
              <button onClick={handleRemoveRule} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444' }} title="Remove Condition">
                <X size={16}/>
              </button>
            </div>
          </BubbleMenu>)}
        {editor && (<BubbleMenu editor={editor} shouldShow={({ editor: e }) => e.isActive('dollarBlock') && !e.isActive('conditionalBlock')}>
            <div style={{ display: 'flex', background: '#fff', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '4px', gap: '4px' }}>
              <button onClick={() => removeMarkFromEnclosingOrSelectedRange(editor, 'dollarBlock')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444' }} title="Remove Row Clear ($) Block">
                <X size={16}/>
              </button>
            </div>
          </BubbleMenu>)}
        {editor && (<BubbleMenu editor={editor} shouldShow={({ editor: e }) => e.isActive('optionalBlock') && !e.isActive('conditionalBlock')}>
            <div style={{ display: 'flex', background: '#fff', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '4px', gap: '4px' }}>
              <button onClick={() => removeMarkFromEnclosingOrSelectedRange(editor, 'optionalBlock')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444' }} title="Remove Optional Block">
                <X size={16}/>
              </button>
            </div>
          </BubbleMenu>)}
        {editor && (<BubbleMenu editor={editor} shouldShow={({ editor: e }) => e.isActive('table')} tippyOptions={{ placement: 'top-start', offset: [0, 8] }}>
            <div style={{ display: 'flex', alignItems: 'center', background: '#fff', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '4px', gap: '4px' }}>
              <button
                onClick={() => handleOpenTableCondition('row')}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '28px', padding: '0 8px', borderRadius: '4px', border: '1px solid #e9d5ff', background: '#faf5ff', color: '#7c3aed', cursor: 'pointer', fontSize: '11.5px', fontWeight: 600 }}
                title="Add or Edit Row Condition (Entire Row)"
              >
                <GitBranch size={13}/>
                <span>Row IF</span>
              </button>
              <button
                onClick={() => handleOpenTableCondition('column')}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '28px', padding: '0 8px', borderRadius: '4px', border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#16a34a', cursor: 'pointer', fontSize: '11.5px', fontWeight: 600 }}
                title="Add or Edit Column Condition (Particular Column)"
              >
                <GitBranch size={13}/>
                <span>Col IF</span>
              </button>
              <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 2px' }}/>
              <button onClick={() => alignStatement(editor, 'left')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#475569' }} title="Align Left (|<=|)">
                <AlignLeft size={16}/>
              </button>
              <button onClick={() => alignStatement(editor, 'center')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#475569' }} title="Align Center (|==|)">
                <AlignCenter size={16}/>
              </button>
              <button onClick={() => alignStatement(editor, 'right')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#475569' }} title="Align Right (|=>|)">
                <AlignRight size={16}/>
              </button>
              <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 2px' }}/>
              <button onClick={() => editor.chain().focus().deleteColumn().run()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '30px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444', fontSize: '12px', fontWeight: 'bold' }} title="Delete Column">
                -Col
              </button>
              <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 2px' }}/>
              <button onClick={() => editor.chain().focus().deleteRow().run()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '30px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444', fontSize: '12px', fontWeight: 'bold' }} title="Delete Row">
                -Row
              </button>
              <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 2px' }}/>
              <button onClick={() => editor.chain().focus().deleteTable().run()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', borderRadius: '4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444' }} title="Delete Table">
                <Trash2 size={16}/>
              </button>
            </div>
          </BubbleMenu>)}
        <EditorContent
          editor={editor}
          className="editor-content"
          style={{ zoom: editorZoom / 100 }}
          onClick={(e) => {
            if (!editor) return;
            const target = e.target;
            const isInteractive = target.closest('button, input, select, textarea, .variable-tag-pill, .variable-tag-wrapper, .conditional-badge-widget');
            if (!isInteractive && !editor.isFocused) {
              editor.commands.focus();
            }
          }}
        />
        {editor && <TableHoverControls editor={editor}/>}
        <style>{`
          .editor-area .tiptap {
            font-family: 'Times New Roman', Times, serif !important;
            font-size: 10pt !important;
            line-height: 1.5 !important;
          }
          .editor-area .tiptap p,
          .editor-area .tiptap span,
          .editor-area .tiptap div,
          .editor-area .tiptap table,
          .editor-area .tiptap td,
          .editor-area .tiptap th,
          .editor-area .tiptap li,
          .editor-area .tiptap ol,
          .editor-area .tiptap ul {
            font-family: 'Times New Roman', Times, serif;
            font-size: 10pt;
            line-height: 1.5;
          }
          .editor-area .tiptap table,
          .editor-area .tiptap td,
          .editor-area .tiptap th {
            text-align: left;
          }
          .editor-area .tiptap td p,
          .editor-area .tiptap th p {
            text-align: inherit;
            line-height: 1.5;
          }
          .editor-area .tiptap p {
            margin: 0;
            min-height: 1.5em;
            line-height: 1.5 !important;
          }
          .editor-area .tiptap h1,
          .editor-area .tiptap h2,
          .editor-area .tiptap h3,
          .editor-area .tiptap h4,
          .editor-area .tiptap h5,
          .editor-area .tiptap h6 {
            font-family: 'Times New Roman', Times, serif !important;
            font-size: 10pt !important;
            margin-top: 1mm;
            margin-bottom: 1mm;
            line-height: 1.5;
          }
          .editor-area .tiptap h1 {
            text-align: center;
            font-weight: normal;
            text-decoration: none;
          }
          .editor-area .tiptap h2 {
            text-align: center;
            font-weight: bold;
            text-decoration: none;
          }
          .editor-area .tiptap h3 {
            text-align: center;
            font-weight: bold;
            text-decoration: underline;
          }
          .editor-search-match {
            background-color: #e9d5ff !important;
            color: #000000 !important;
            border-radius: 2px;
            padding: 1px 2px;
          }
          .editor-search-match.is-active {
            background-color: #c084fc !important;
            color: #000000 !important;
            outline: 2px solid #7c3aed !important;
            box-shadow: 0 0 0 2px #7c3aed !important;
            border-radius: 2px;
          }
          .variable-tag-wrapper.editor-search-match .variable-tag-pill {
            background: #e9d5ff !important;
            border-color: #c084fc !important;
            color: #000000 !important;
          }
          .variable-tag-wrapper.editor-search-match.is-active .variable-tag-pill {
            background: #c084fc !important;
            border-color: #7c3aed !important;
            color: #000000 !important;
            box-shadow: 0 0 0 2px #7c3aed !important;
          }
          div[data-type="white-blank-image"].editor-search-match {
            outline: 2px dashed #c084fc !important;
            background-color: rgba(233, 213, 255, 0.3) !important;
          }
          div[data-type="white-blank-image"].editor-search-match.is-active {
            outline: 2px solid #7c3aed !important;
            box-shadow: 0 0 0 2px #7c3aed !important;
            background-color: rgba(192, 132, 252, 0.4) !important;
          }
          .search-doc-input {
            color: #ffffff !important;
          }
          .search-doc-input::placeholder {
            color: rgba(255, 255, 255, 0.85) !important;
          }
        `}</style>
      </div>

      {/* Bottom Bar: Zoom in & out controls at the bottom right */}
      <div className="editor-bottom-bar" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '6px 20px',
        backgroundColor: '#ffffff',
        borderTop: '1px solid #e2e8f0',
        flexShrink: 0,
        boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.02)'
      }}>
        {/* Zoom Controls at the bottom right */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: '#ffffff',
          border: '1px solid #cbd5e1',
          borderRadius: '8px',
          padding: '3px 8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
        }}>
          <button
            type="button"
            onClick={handleZoomOut}
            disabled={editorZoom <= 50}
            title="Zoom Out (-10%)"
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              cursor: editorZoom <= 50 ? 'not-allowed' : 'pointer',
              opacity: editorZoom <= 50 ? 0.4 : 1,
              padding: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--accent-teal, #068989)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { if (editorZoom > 50) e.currentTarget.style.backgroundColor = 'rgba(6, 137, 137, 0.08)'; }}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            <ZoomOut size={16} />
          </button>
          <button
            type="button"
            onClick={handleResetZoom}
            title="Reset Zoom to 100%"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              color: '#334155',
              minWidth: '46px',
              textAlign: 'center',
              padding: '2px 4px',
              borderRadius: '4px',
              transition: 'background-color 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {editorZoom}%
          </button>
          <button
            type="button"
            onClick={handleZoomIn}
            disabled={editorZoom >= 200}
            title="Zoom In (+10%)"
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              cursor: editorZoom >= 200 ? 'not-allowed' : 'pointer',
              opacity: editorZoom >= 200 ? 0.4 : 1,
              padding: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--accent-teal, #068989)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { if (editorZoom < 200) e.currentTarget.style.backgroundColor = 'rgba(6, 137, 137, 0.08)'; }}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
          >
            <ZoomIn size={16} />
          </button>
        </div>
      </div>

      <RuleModal
        isOpen={isRuleModalOpen}
        onClose={closeRuleModal}
        onSave={handleSaveRule}
        onRemove={handleRemoveRule}
        variables={variables}
        initialData={editingRuleData}
        tableTargetOptions={tableTargetOptions}
      />

      <SyntaxHelpPanel isOpen={isSyntaxHelpOpen} onClose={() => setIsSyntaxHelpOpen(false)}/>
    </div>);
}
